import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Gavel,
  ShieldAlert,
  Scale,
  Plus,
  Search,
  FileText,
  Building2,
  Landmark,
  CheckCircle2,
  Clock,
  Eye,
  AlertTriangle,
  RotateCcw,
  FileCheck2,
  ExternalLink,
  DollarSign,
  Download,
  Copy
} from 'lucide-react';
import { ExecutiveLawsuit, LawsuitStage } from './LitigationTypes';
import { ExecutiveLawsuitGenerator } from './ExecutiveLawsuitGenerator';
import { toast } from 'sonner';

interface DebtorOption {
  id?: string;
  copropiedad_nombre?: string;
  direccion?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  dias_mora?: number;
  etapa_cobro?: string;
}

interface LitigationTabProps {
  debtors: DebtorOption[];
  copropiedadNombre?: string;
  copropiedadNit?: string;
  administradorNombre?: string;
}

const STORAGE_KEY = 'legal_notifications_lawsuits_v1';

// Demandas de ejemplo realistas pre-cargadas
const DEFAULT_LAWSUITS: ExecutiveLawsuit[] = [
  {
    id: 'DEM-804123',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    ciudad: 'Bogotá D.C.',
    departamento: 'Cundinamarca',
    juzgado_reparto: 'Juez Civil Municipal de Bogotá (Reparto)',
    administrador_nombre: 'Administración PH',
    administrador_cedula: '52.148.963',
    abogado: {
      nombre: 'Dr. Alejandro Morales Restrepo',
      cedula: '80.123.456',
      tarjetaProfesional: '245.890 C.S.J.',
      correoJudicial: 'alejandro.morales.abogado@notificacionesjudiciales.co',
      telefono: '3158901234',
      direccionDespacho: 'Carrera 7 No. 72 - 13 Oficina 802, Bogotá D.C.'
    },
    unidad_privada: 'Torre 3 Apto 802',
    propietario_nombre: 'Gonzalo Silva Echeverry',
    propietario_id: '19456782',
    propietario_correo: 'gonzalo.silva@outlook.com',
    propietario_telefono: '3109876543',
    direccion_notificacion: 'Calle 127 No. 19 - 45, Torre 3 Apto 802, Bogotá D.C.',
    capital_adeudado: 4500000,
    intereses_moratorios_a_la_fecha: 1250000,
    cuantia_total: 5750000,
    tipo_cuantia: 'minima',
    meses_adeudados_descripcion: 'Expensas ordinarias y cuota extraordinaria de fachada (11 meses)',
    medidas_cautelares: {
      embargoInmueble: true,
      matriculaInmobiliaria: '50C-1845920',
      direccionInmueble: 'Calle 127 No. 19 - 45, Torre 3 Apto 802',
      embargoCuentasBancarias: true,
      limiteEmbargoCuentas: 11500000,
      entidadesBancarias: ['Bancolombia S.A.', 'Banco Davivienda S.A.'],
      embargoSalarios: false
    },
    anexos_verificados: {
      poderEspecial: true,
      certificadoExistenciaAlcaldia: true,
      tituloEjecutivoCertificacion: true,
      certificadoTradicionLibertad: true,
      liquidacioCreditoDetallada: true,
      pruebaEnvioNotificacionLey2213: true
    },
    libelo_demanda: 'DEMANDA EJECUTIVA SINGULAR FORMAL',
    solicitud_medidas_cautelares: 'MEDIDAS CAUTELARES PREVIAS',
    poder_especial: 'PODER ESPECIAL AMPLIO Y SUFICIENTE',
    estado: 'mandamiento_librado',
    numero_radicado_rama: '11001400301420260048200',
    juzgado_asignado: 'Juzgado 14 Civil Municipal de Bogotá',
    fecha_radicacion: '2026-09-10',
    fecha_mandamiento: '2026-09-28',
    notas_procesales: 'Auto de mandamiento de pago librado con éxito. Oficio de embargo remitido a la Oficina de Registro (ORIP Zona Norte).',
    created_at: '2026-09-10T10:00:00Z',
    updated_at: '2026-09-28T16:00:00Z'
  },
  {
    id: 'DEM-791024',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    ciudad: 'Bogotá D.C.',
    departamento: 'Cundinamarca',
    juzgado_reparto: 'Juez Civil Municipal de Bogotá (Reparto)',
    administrador_nombre: 'Administración PH',
    administrador_cedula: '52.148.963',
    abogado: {
      nombre: 'Dr. Alejandro Morales Restrepo',
      cedula: '80.123.456',
      tarjetaProfesional: '245.890 C.S.J.',
      correoJudicial: 'alejandro.morales.abogado@notificacionesjudiciales.co',
      telefono: '3158901234',
      direccionDespacho: 'Carrera 7 No. 72 - 13 Oficina 802, Bogotá D.C.'
    },
    unidad_privada: 'Torre 1 Apto 104',
    propietario_nombre: 'Inversiones & Activos SAS',
    propietario_id: '901234567-1',
    propietario_correo: 'juridico@inversionesactivos.com',
    propietario_telefono: '3008901234',
    direccion_notificacion: 'Calle 127 No. 19 - 45, Torre 1 Apto 104, Bogotá D.C.',
    capital_adeudado: 7200000,
    intereses_moratorios_a_la_fecha: 1850000,
    cuantia_total: 9050000,
    tipo_cuantia: 'minima',
    meses_adeudados_descripcion: 'Expensas ordinarias de administración últimos 14 meses',
    medidas_cautelares: {
      embargoInmueble: true,
      matriculaInmobiliaria: '50C-1748291',
      direccionInmueble: 'Calle 127 No. 19 - 45, Torre 1 Apto 104',
      embargoCuentasBancarias: true,
      limiteEmbargoCuentas: 18100000,
      entidadesBancarias: ['Bancolombia S.A.', 'Banco de Bogotá S.A.'],
      embargoSalarios: false
    },
    anexos_verificados: {
      poderEspecial: true,
      certificadoExistenciaAlcaldia: true,
      tituloEjecutivoCertificacion: true,
      certificadoTradicionLibertad: true,
      liquidacioCreditoDetallada: true,
      pruebaEnvioNotificacionLey2213: true
    },
    libelo_demanda: 'DEMANDA EJECUTIVA SINGULAR FORMAL',
    solicitud_medidas_cautelares: 'MEDIDAS CAUTELARES PREVIAS',
    poder_especial: 'PODER ESPECIAL AMPLIO Y SUFICIENTE',
    estado: 'listo_para_radicar',
    created_at: '2026-10-18T11:00:00Z',
    updated_at: '2026-10-18T11:00:00Z'
  }
];

export const LitigationTab: React.FC<LitigationTabProps> = ({
  debtors,
  copropiedadNombre = 'Conjunto Residencial Torres del Parque',
  copropiedadNit = '900.852.147-3',
  administradorNombre = 'Administración PH'
}) => {
  const [lawsuits, setLawsuits] = useState<ExecutiveLawsuit[]>([]);
  const [activeView, setActiveView] = useState<'list' | 'candidates' | 'generator'>('list');
  const [selectedDebtorForLawsuit, setSelectedDebtorForLawsuit] = useState<DebtorOption | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('todos');

  // Modal para ver detalle / actualizar radicado de demanda
  const [viewingLawsuit, setViewingLawsuit] = useState<ExecutiveLawsuit | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingRadicado, setEditingRadicado] = useState('');
  const [editingJuzgado, setEditingJuzgado] = useState('');
  const [editingEstado, setEditingEstado] = useState<LawsuitStage>('listo_para_radicar');

  // Cargar demandas desde localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setLawsuits(parsed);
          return;
        }
      }
      setLawsuits(DEFAULT_LAWSUITS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_LAWSUITS));
    } catch (err) {
      console.warn('Error loading lawsuits from localStorage:', err);
      setLawsuits(DEFAULT_LAWSUITS);
    }
  }, []);

  const saveLawsuits = (updated: ExecutiveLawsuit[]) => {
    setLawsuits(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (err) {
      console.warn('Error saving lawsuits to localStorage:', err);
    }
  };

  // Candidatos a Demanda (Unidades con más de 60 días de mora o en etapa prejudicial)
  const candidateDebtors = useMemo(() => {
    return debtors.filter(d => {
      const dias = d.dias_mora || 0;
      const isCritical = dias >= 60 || d.etapa_cobro === 'prejudicial_aviso_demanda';
      // Que no tenga ya demanda activa
      const alreadyHasLawsuit = lawsuits.some(l => l.unidad_privada === d.unidad_privada && l.estado !== 'terminado_por_pago');
      return isCritical && !alreadyHasLawsuit;
    });
  }, [debtors, lawsuits]);

  // KPIs
  const stats = useMemo(() => {
    const total = lawsuits.length;
    const activos = lawsuits.filter(l => l.estado !== 'terminado_por_pago').length;
    const capitalEnLitigio = lawsuits.reduce((acc, l) => acc + (l.cuantia_total || 0), 0);
    const conEmbargo = lawsuits.filter(l => l.medidas_cautelares.embargoInmueble).length;
    const conMandamiento = lawsuits.filter(l => l.estado === 'mandamiento_librado' || l.estado === 'medidas_decretadas').length;

    return {
      total,
      activos,
      capitalEnLitigio,
      conEmbargo,
      conMandamiento
    };
  }, [lawsuits]);

  // Filtrado de demandas
  const filteredLawsuits = useMemo(() => {
    return lawsuits.filter(l => {
      const matchesSearch =
        l.unidad_privada.toLowerCase().includes(searchQuery.toLowerCase()) ||
        l.propietario_nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (l.copropiedad_nombre && l.copropiedad_nombre.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (l.numero_radicado_rama && l.numero_radicado_rama.includes(searchQuery));

      if (!matchesSearch) return false;
      if (stageFilter === 'todos') return true;
      return l.estado === stageFilter;
    });
  }, [lawsuits, searchQuery, stageFilter]);

  // Iniciar generador con un deudor
  const handleStartLawsuitForDebtor = (debtor: DebtorOption) => {
    setSelectedDebtorForLawsuit(debtor);
    setActiveView('generator');
  };

  // Guardar nueva demanda
  const handleSaveCreatedLawsuit = (newLawsuit: ExecutiveLawsuit) => {
    const updated = [newLawsuit, ...lawsuits];
    saveLawsuits(updated);
    setActiveView('list');
  };

  // Abrir modal de detalle/edición
  const handleOpenDetailModal = (lawsuit: ExecutiveLawsuit) => {
    setViewingLawsuit(lawsuit);
    setEditingRadicado(lawsuit.numero_radicado_rama || '');
    setEditingJuzgado(lawsuit.juzgado_asignado || '');
    setEditingEstado(lawsuit.estado);
    setIsDetailModalOpen(true);
  };

  // Guardar actualización de estado / radicado
  const handleSaveLawsuitUpdates = () => {
    if (!viewingLawsuit) return;
    const updated = lawsuits.map(l => {
      if (l.id === viewingLawsuit.id) {
        return {
          ...l,
          numero_radicado_rama: editingRadicado,
          juzgado_asignado: editingJuzgado,
          estado: editingEstado,
          updated_at: new Date().toISOString()
        };
      }
      return l;
    });
    saveLawsuits(updated);
    setIsDetailModalOpen(false);
    toast.success('Datos procesales actualizados con éxito.');
  };

  const getStageBadge = (stage: LawsuitStage) => {
    switch (stage) {
      case 'listo_para_radicar':
        return <Badge className="bg-amber-500 text-slate-950 font-bold text-[10px]">Listo para Radicar</Badge>;
      case 'radicado_esperando_reparto':
        return <Badge className="bg-sky-600 text-white text-[10px]">Radicado / En Reparto</Badge>;
      case 'mandamiento_librado':
        return <Badge className="bg-emerald-600 text-white text-[10px] font-bold">Mandamiento Librado</Badge>;
      case 'medidas_decretadas':
        return <Badge className="bg-indigo-600 text-white text-[10px] font-bold">Embargo Decretado</Badge>;
      case 'terminado_por_pago':
        return <Badge variant="outline" className="border-emerald-400 text-emerald-700 text-[10px]">Terminado por Pago</Badge>;
      default:
        return <Badge variant="outline" className="text-[10px]">{stage}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* ── HEADER Y NAVEGACIÓN DE VISTAS ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 text-slate-900 dark:text-white">
            <Gavel className="h-5 w-5 text-indigo-600" />
            <span>Módulo de Cobro Jurídico, Demandas Ejecutivas &amp; Embargos</span>
            <Badge className="bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 border-indigo-300 text-[11px]">
              Código General del Proceso (CGP)
            </Badge>
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Automatización de demandas ejecutivas, medidas cautelares previas sobre inmuebles y cuentas bancarias, y seguimiento procesal judicial.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeView === 'generator' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveView('list')}
              className="text-xs h-8 gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Volver al Panel Jurídico
            </Button>
          ) : (
            <>
              <Button
                variant={activeView === 'candidates' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveView(activeView === 'candidates' ? 'list' : 'candidates')}
                className={`text-xs h-8 gap-1.5 relative ${activeView === 'candidates' ? 'bg-indigo-600 text-white' : ''}`}
              >
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                Candidatos a Demanda
                {candidateDebtors.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-rose-600 text-white font-bold">
                    {candidateDebtors.length}
                  </span>
                )}
              </Button>

              <Button
                size="sm"
                onClick={() => {
                  const firstDebtor = candidateDebtors[0] || debtors[0] || {
                    unidad_privada: 'Torre 2 Apto 504',
                    propietario_nombre: 'Copropietario Moroso',
                    saldo_capital: 3500000,
                    saldo_intereses: 900000
                  };
                  handleStartLawsuitForDebtor(firstDebtor);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8 gap-1.5 font-semibold shadow-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                Redactar Nueva Demanda
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── KPIS DE LITIGIO JUDICIAL ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Demandas en Trámite
              </span>
              <span className="text-2xl font-black text-slate-900 dark:text-white">
                {stats.activos}
              </span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">
                {stats.total} radicadas en histórico
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400">
              <Gavel className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Capital en Litigio
              </span>
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                ${(stats.capitalEnLitigio / 1000000).toFixed(1)}M
              </span>
              <span className="text-[10px] text-indigo-600 block mt-0.5">
                ${stats.capitalEnLitigio.toLocaleString('es-CO')} COP
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
              <DollarSign className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Medidas Cautelares
              </span>
              <span className="text-2xl font-black text-rose-600">
                {stats.conEmbargo}
              </span>
              <span className="text-[10px] text-rose-600 block mt-0.5 font-medium">
                Embargos solicitados (ORIP / Bancos)
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400">
              <ShieldAlert className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Mandamientos Librados
              </span>
              <span className="text-2xl font-black text-emerald-600">
                {stats.conMandamiento}
              </span>
              <span className="text-[10px] text-emerald-600 block mt-0.5 font-medium">
                Orden judicial de pago en firme
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── VISTAS: GENERADOR VS CANDIDATOS VS LISTADO DE DEMANDAS ── */}
      {activeView === 'generator' && selectedDebtorForLawsuit ? (
        <ExecutiveLawsuitGenerator
          debtor={selectedDebtorForLawsuit}
          copropiedadNombre={copropiedadNombre}
          copropiedadNit={copropiedadNit}
          administradorNombre={administradorNombre}
          onSaveLawsuit={handleSaveCreatedLawsuit}
          onCancel={() => setActiveView('list')}
        />
      ) : activeView === 'candidates' ? (
        /* Tabla de Candidatos a Demanda (Mora Crítica) */
        <Card className="border shadow-sm border-amber-300 dark:border-amber-900/60">
          <CardHeader className="pb-3 bg-amber-50/60 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-900/40">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Inmuebles en Estado Crítico para Radicación de Demanda
                </CardTitle>
                <CardDescription className="text-xs">
                  Unidades con más de 60 días de mora o sin acuerdo vigente que ameritan cobro coactivo judicial inmediato con embargo de inmueble.
                </CardDescription>
              </div>
              <Badge className="bg-amber-600 text-white text-xs">
                {candidateDebtors.length} unidades en mora severa
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {candidateDebtors.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground space-y-2">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500" />
                <p className="text-sm font-medium">No hay deudores en estado crítico pendientes de demanda.</p>
                <p className="text-xs">Todos los copropietarios morosos están cubiertos con acuerdos o en mora temprana.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-muted/30">
                    <TableHead>PH / Conjunto</TableHead>
                    <TableHead>Inmueble</TableHead>
                    <TableHead>Propietario</TableHead>
                    <TableHead>Saldo Capital</TableHead>
                    <TableHead>Intereses</TableHead>
                    <TableHead>Días de Mora</TableHead>
                    <TableHead>Etapa Actual</TableHead>
                    <TableHead className="text-right">Acción Jurídica</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidateDebtors.map(debtor => {
                    const tot = (debtor.saldo_capital || 0) + (debtor.saldo_intereses || 0);
                    return (
                      <TableRow key={debtor.id || debtor.unidad_privada} className="text-xs hover:bg-muted/30">
                        <TableCell className="font-medium text-foreground whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                            <span>{debtor.copropiedad_nombre || copropiedadNombre || 'Copropiedad'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-bold text-foreground whitespace-nowrap">{debtor.unidad_privada}</TableCell>
                        <TableCell>
                          <span className="font-medium">{debtor.propietario_nombre}</span>
                          <span className="text-[10px] text-muted-foreground block font-mono">C.C. {debtor.propietario_id || '-'}</span>
                        </TableCell>
                        <TableCell className="font-semibold">${debtor.saldo_capital.toLocaleString('es-CO')}</TableCell>
                        <TableCell className="text-rose-600 font-semibold">${debtor.saldo_intereses.toLocaleString('es-CO')}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-rose-400 text-rose-700 bg-rose-50 text-[10px]">
                            {debtor.dias_mora} días
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-rose-600 text-white text-[10px]">
                            {debtor.etapa_cobro || 'prejudicial'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handleStartLawsuitForDebtor(debtor)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7 gap-1 font-semibold shadow-xs"
                          >
                            <Gavel className="h-3 w-3" /> Demandar en 1 Clic
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Listado Principal de Demandas Radicadas / Expedientes */
        <Card className="border shadow-sm">
          <CardHeader className="pb-3 border-b bg-muted/10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por Torre/Apto, Demandado o Radicado Rama..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 text-xs h-8"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">Estado:</span>
                <select
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value)}
                  className="text-xs h-8 rounded-md border border-input bg-background px-2.5 shadow-sm"
                >
                  <option value="todos">Todos los estados</option>
                  <option value="listo_para_radicar">Listos para Radicar</option>
                  <option value="mandamiento_librado">Mandamiento Librado</option>
                  <option value="medidas_decretadas">Embargos Decretados</option>
                  <option value="terminado_por_pago">Terminados por Pago</option>
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {filteredLawsuits.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground space-y-2">
                <Gavel className="h-8 w-8 mx-auto text-muted-foreground/40" />
                <p className="text-sm font-medium">No hay demandas judiciales registradas con los filtros seleccionados.</p>
                <Button
                  size="sm"
                  onClick={() => setActiveView('candidates')}
                  className="text-xs h-8 mt-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  Ver Candidatos a Demanda
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/30">
                      <TableHead>PH / Conjunto</TableHead>
                      <TableHead>Inmueble</TableHead>
                      <TableHead>Demandado(a)</TableHead>
                      <TableHead>Cuantía Total</TableHead>
                      <TableHead>Radicado Rama Judicial</TableHead>
                      <TableHead>Juzgado Asignado</TableHead>
                      <TableHead>Medidas Cautelares</TableHead>
                      <TableHead>Estado Procesal</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLawsuits.map((lawsuit) => (
                      <TableRow key={lawsuit.id} className="text-xs hover:bg-muted/30">
                        <TableCell className="font-medium text-foreground whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                            <span>{lawsuit.copropiedad_nombre || copropiedadNombre || 'Copropiedad'}</span>
                          </div>
                        </TableCell>

                        <TableCell className="font-bold text-foreground whitespace-nowrap">
                          {lawsuit.unidad_privada}
                        </TableCell>

                        <TableCell>
                          <div className="space-y-0.5">
                            <span className="font-semibold block">{lawsuit.propietario_nombre}</span>
                            <span className="text-[10px] text-muted-foreground font-mono">
                              C.C./NIT: {lawsuit.propietario_id}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div>
                            <span className="font-bold text-indigo-700 dark:text-indigo-300 block">
                              ${lawsuit.cuantia_total.toLocaleString('es-CO')}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Capital: ${lawsuit.capital_adeudado.toLocaleString('es-CO')}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell>
                          {lawsuit.numero_radicado_rama ? (
                            <span className="font-mono text-[11px] font-semibold text-slate-800 dark:text-slate-200 bg-muted px-2 py-0.5 rounded">
                              {lawsuit.numero_radicado_rama}
                            </span>
                          ) : (
                            <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-300 bg-amber-50">
                              Pendiente Radicación
                            </Badge>
                          )}
                        </TableCell>

                        <TableCell>
                          <span className="text-slate-700 dark:text-slate-300 font-medium">
                            {lawsuit.juzgado_asignado || lawsuit.juzgado_reparto}
                          </span>
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-1">
                            {lawsuit.medidas_cautelares.embargoInmueble && (
                              <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 border-rose-300 text-[9px]">
                                Embargo Inmueble
                              </Badge>
                            )}
                            {lawsuit.medidas_cautelares.embargoCuentasBancarias && (
                              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300 text-[9px]">
                                Cuentas
                              </Badge>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          {getStageBadge(lawsuit.estado)}
                        </TableCell>

                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenDetailModal(lawsuit)}
                            className="h-7 text-xs gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950"
                          >
                            <Eye className="h-3 w-3" /> Ver Expediente
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── MODAL: VER EXPEDIENTE Y ACTUALIZAR RADICADO / ESTADO JUDICIAL ── */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gavel className="h-5 w-5 text-indigo-600" />
                <span>Expediente Judicial: {viewingLawsuit?.unidad_privada} ({viewingLawsuit?.propietario_nombre})</span>
              </div>
              <Badge className="bg-indigo-600 text-white text-xs">
                {viewingLawsuit?.id}
              </Badge>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Cuantía: ${viewingLawsuit?.cuantia_total.toLocaleString('es-CO')} COP &bull; Demandante: {copropiedadNombre}
            </DialogDescription>
          </DialogHeader>

          {viewingLawsuit && (
            <div className="space-y-4 py-2 text-xs">
              <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border bg-slate-50 dark:bg-slate-900">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Número de Radicado Rama Judicial (23 dígitos)</Label>
                  <Input
                    placeholder="Ej: 11001400301420260048200"
                    value={editingRadicado}
                    onChange={(e) => setEditingRadicado(e.target.value)}
                    className="text-xs h-8 font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Juzgado Asignado</Label>
                  <Input
                    placeholder="Ej: Juzgado 14 Civil Municipal de Bogotá"
                    value={editingJuzgado}
                    onChange={(e) => setEditingJuzgado(e.target.value)}
                    className="text-xs h-8"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs font-semibold">Estado Procesal Actual</Label>
                <select
                  value={editingEstado}
                  onChange={(e: any) => setEditingEstado(e.target.value)}
                  className="w-full text-xs h-8 rounded-md border border-input bg-background px-3 shadow-sm font-semibold"
                >
                  <option value="listo_para_radicar">Listo para Radicar</option>
                  <option value="radicado_esperando_reparto">Radicado / Esperando Reparto</option>
                  <option value="mandamiento_librado">Mandamiento de Pago Librado</option>
                  <option value="medidas_decretadas">Medidas Cautelares Decretadas (Oficios librados)</option>
                  <option value="embargo_consumado">Embargo Registrado en ORIP / Cuentas Retenidas</option>
                  <option value="terminado_por_pago">Terminado por Pago / Finiquitado</option>
                </select>
              </div>

              {/* Botonera de Acciones de Descarga */}
              <div className="pt-2 border-t flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(viewingLawsuit.libelo_demanda);
                      toast.success('Demanda copiada al portapapeles.');
                    }}
                    className="h-8 text-xs gap-1"
                  >
                    <Copy className="h-3 w-3" /> Copiar Demanda
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const blob = new Blob([viewingLawsuit.libelo_demanda], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Demanda_${viewingLawsuit.unidad_privada.replace(/\s+/g, '_')}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Demanda descargada en TXT.');
                    }}
                    className="h-8 text-xs gap-1"
                  >
                    <Download className="h-3 w-3" /> Descargar Demanda
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const blob = new Blob([viewingLawsuit.solicitud_medidas_cautelares], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Medidas_${viewingLawsuit.unidad_privada.replace(/\s+/g, '_')}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Medidas cautelares descargadas en TXT.');
                    }}
                    className="h-8 text-xs gap-1"
                  >
                    <ShieldAlert className="h-3 w-3 text-rose-600" /> Descargar Medidas
                  </Button>
                </div>

                <Button
                  size="sm"
                  onClick={handleSaveLawsuitUpdates}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8 font-semibold"
                >
                  Guardar Cambios Procesales
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default LitigationTab;
